// ============================================================
// Catalog list mock data — derived from SEED_OPPS so the list and
// detail pages share a single source of truth.
//
// Pre-2026-06-01 the catalog kept its own MOCK_OPPORTUNITIES array,
// which drifted from the detail-page SEED_OPPS (different IDs,
// missing combos). Clicking a row in a status combo that only existed
// in the catalog (e.g. UPSIZE / CANCELLED) 404'd because
// `findOppById()` reads SEED_OPPS. Unifying through this projection
// makes the bug structurally impossible.
//
// The list only needs a thin slice (catalog Opportunity below); the
// detail page reads the full SEED_OPPS shape directly via
// `findOppById`. To add a new row, edit SEED_OPPS — it shows up here
// automatically.
// ============================================================

import { SEED_OPPS } from '../CampaignDetail/opportunity.mock';
import type { Opportunity as SeedOpportunity } from '../CampaignDetail/opportunity';
import { numericIdFor } from '../../utils/numericId';
import type { Opportunity, CatalogPackage } from './types';

export type { OpportunityType, OpportunityStatus, CatalogPackage, Opportunity } from './types';

// ── Helpers ──────────────────────────────────────────────────

const toDateString = (d: Date | string | null | undefined): string | null => {
  if (!d) return null;
  if (typeof d === 'string') {
    // Some seed entries store dates as ISO strings; pass through.
    return d.slice(0, 10);
  }
  // Pad month/day to 2 digits — local time, since catalog filtering is
  // calendar-day granularity not timezone-sensitive.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Catalog "Dates" column format per STATES.md §8.4:
//   • DRAFT or missing dates → "TBD"
//   • same month + year      → "01 – 30 Jun 2026"   (year + month collapsed)
//   • cross month / year     → "01 Jun – 02 Aug 2026"
// Uses the same "dd MMM yyyy" tokens fmtDate uses in CampaignDetail.
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateRange = (start: Date | string | null | undefined, end: Date | string | null | undefined): string => {
  const s = start instanceof Date ? start : start ? new Date(start) : null;
  const e = end instanceof Date ? end : end ? new Date(end) : null;
  if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return 'TBD';
  const sd = String(s.getDate()).padStart(2, '0');
  const ed = String(e.getDate()).padStart(2, '0');
  const sm = MONTH_NAMES[s.getMonth()];
  const em = MONTH_NAMES[e.getMonth()];
  const sy = s.getFullYear();
  const ey = e.getFullYear();
  if (sm === em && sy === ey) return `${sd} – ${ed} ${em} ${ey}`;
  if (sy === ey) return `${sd} ${sm} – ${ed} ${em} ${ey}`;
  return `${sd} ${sm} ${sy} – ${ed} ${em} ${ey}`;
};

const toCatalogRow = (s: SeedOpportunity): Opportunity => {
  // SEED_OPPS doesn't carry startTime / endTime / timezone / subtype
  // (the detail page doesn't render them). Catalog needs them for the
  // row's date-range chip — default to a full-window 00:00–23:59 in
  // UTC+8 so the list reads cleanly. Real wizard submissions will land
  // with explicit times once the wizard wires that field through.

  // For AD_SALES, surface the per-package summary so the catalog can
  // group-render multi-package campaigns. Seed entries store packages
  // under `selected[]` with packageName / startDate / endDate per row.
  // Status isn't tracked per-package in seed — fall back to the parent
  // campaign status; `derivePackageStatus()` in the UI will translate
  // APPROVED → COMPLETED for packages whose endDate is in the past.
  let packages: CatalogPackage[] | undefined;
  if (s.type === 'AD_SALES') {
    const selected = (s as unknown as { selected?: Array<Record<string, unknown>> }).selected || [];
    packages = selected.map((pkg, i) => ({
      id: String(pkg.instanceId || pkg.packageId || `${s.id}-pkg-${i}`),
      packageType: 'STANDARD_PACKAGE',
      status: s.status,
      startDate: toDateString(pkg.startDate as Date | string | null | undefined),
      endDate: toDateString(pkg.endDate as Date | string | null | undefined),
      dates: formatDateRange(
        pkg.startDate as Date | string | null | undefined,
        pkg.endDate as Date | string | null | undefined
      ),
      name: typeof pkg.packageName === 'string' ? pkg.packageName : undefined,
    }));
  }

  return {
    id: s.id,
    numericId: numericIdFor(s),
    type: s.type,
    brand: s.brand,
    status: s.status,
    startDate: toDateString(s.startDate),
    endDate: toDateString(s.endDate),
    startTime: '00:00',
    endTime: '23:59',
    timezone: 'UTC+8',
    creationSource: s.creationSource ?? undefined,
    submittedAt: s.submittedAt ?? null,
    // SEED_OPPS doesn't carry the subtype label the catalog tag column
    // renders. Left null until SEED_OPPS grows a subtype field; for now
    // the row's Type tag shows the bare type name only.
    subtype: null,
    // Pre-computed range string for the catalog Dates column. DRAFT rows
    // (which carry null startDate/endDate) collapse to "TBD" per the
    // STATES.md §8.4 convention.
    dates: formatDateRange(s.startDate, s.endDate),
    ...(packages && { packages }),
  };
};

// Derived list. Stable insertion order from SEED_OPPS; CampaignCatalog
// applies its own 3-level sort (type → status → submittedAt desc) on
// top of this.
export const MOCK_OPPORTUNITIES: Opportunity[] = SEED_OPPS.map(toCatalogRow);

export const MOCK_BRANDS = ['Acme Commerce', 'Acme Fashion', 'Acme Home & Living'];
